/**
 * The whole organisation's audit log, searched (PRD 4's compliance officer:
 * "searchable, exportable log of all task completions with actor, timestamp,
 * and employee"). HR, admins and compliance search it; admins and compliance
 * export what they found as CSV, and the export is itself a row in the log
 * (PRD 11.2). The Stage 4 gate is a question asked here: who finished this
 * person's I-9, and when.
 */

import { useEffect, useState, type FormEvent } from "react";
import {
  AUDIT_KINDS,
  EMPTY_AUDIT_QUERY,
  type AuditHit,
  type AuditKind,
  type AuditQuery,
  type AuditSearchResponse,
} from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { save } from "../lib/download.js";
import { useOrg } from "../lib/org.js";
import { boardOf } from "../lib/router.js";
import { plural, whenText } from "../lib/words.js";
import { describe } from "./AuditLog.js";
import { EmptyState, Link, Loading, PageHeader, Problem } from "./bits.js";

/** A date input's day as this browser's midnight, or the last millisecond of it. */
function dayBound(day: string, end: boolean): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  const [, year, month, date] = match.map(Number) as [number, number, number, number];
  return new Date(year, month - 1, date + (end ? 1 : 0)).getTime() - (end ? 1 : 0);
}

function Row({ hit, nameOf }: { hit: AuditHit; nameOf: (id: string | null) => string }) {
  const who = hit.actorId === "system" ? "utuwrk, by itself" : hit.actorName || nameOf(hit.actorId);
  return (
    <li>
      <span className="audit-what">
        {hit.taskTitle ? <span className="audit-task">{hit.taskTitle}: </span> : null}
        {describe(hit, nameOf)}
      </span>
      {hit.employeeName ? (
        <span className="audit-note">
          {hit.instanceId && hit.kind ? (
            <Link to={boardOf(hit.instanceId, hit.kind)}>{hit.employeeName}</Link>
          ) : (
            hit.employeeName
          )}
          {hit.kind ? `'s ${hit.kind}` : ""}
          {hit.siteName ? `, ${hit.siteName}` : ""}
        </span>
      ) : null}
      <span className="muted audit-who">
        {who}
        {hit.actorDepartment ? ` (${hit.actorDepartment})` : ""}, {whenText(hit.at)}
      </span>
    </li>
  );
}

export function AuditSearch() {
  const { api, nameOf } = useOrg();
  const [text, setText] = useState("");
  const [kind, setKind] = useState<AuditKind>("all");
  const [siteId, setSiteId] = useState("");
  const [department, setDepartment] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [asked, setAsked] = useState<AuditQuery>(EMPTY_AUDIT_QUERY);
  const [data, setData] = useState<AuditSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [exported, setExported] = useState<string | null>(null);

  const query = (): AuditQuery => ({
    text: text.trim(),
    kind,
    siteId: siteId || null,
    department: department || null,
    since: dayBound(from, false),
    until: dayBound(to, true),
  });

  const run = async (next: AuditQuery, before: number | null) => {
    setBusy(true);
    setError(null);
    try {
      const page = await api.auditSearch(next, before);
      setAsked(next);
      setData((shown) =>
        before !== null && shown ? { ...page, hits: [...shown.hits, ...page.hits] } : page,
      );
    } catch (problem) {
      setError(sentence(problem));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void run(EMPTY_AUDIT_QUERY, null);
    // Once, on arrival: everything after is a search somebody asked for.
  }, [api]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setExported(null);
    void run(query(), null);
  };

  const exportCsv = async () => {
    setBusy(true);
    setError(null);
    try {
      const file = await api.auditExport(asked);
      save(file.filename, file.csv, "text/csv");
      setExported(`Exported ${plural(file.rows, "row")}. The export is in the log too.`);
    } catch (problem) {
      setError(sentence(problem));
    } finally {
      setBusy(false);
    }
  };

  if (!data && !error) return <Loading title="Audit log" />;

  return (
    <section className="page">
      <PageHeader
        title="Audit log"
        meta="Every change in every onboarding and offboarding. Nobody can edit or delete it, administrators included."
        actions={
          data?.canExport ? (
            <button type="button" className="button button-quiet" onClick={exportCsv} disabled={busy}>
              Export CSV
            </button>
          ) : undefined
        }
      />
      <Problem>{error}</Problem>
      {exported ? (
        <p className="pill s-sent" role="status">
          {exported}
        </p>
      ) : null}
      {data && !data.intact ? (
        <p className="problem" role="alert">
          A row does not match the chain, or the log no longer reaches the last row recorded outside it. The
          log has been changed underneath utuwrk; report this.
        </p>
      ) : null}
      {data?.anchor && data.intact ? (
        <p className="muted audit-who">
          Every row checks out against the one before it. Row {data.anchor.seq} was recorded outside this log{" "}
          {whenText(data.anchor.at)} (fingerprint {data.anchor.hash.slice(0, 12)}), so rows cut from the end
          would show too.
        </p>
      ) : null}

      <form className="panel" onSubmit={submit}>
        <div className="controls">
          <label className="field field-wide">
            <span>Find</span>
            <input
              type="search"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="A person, a card or who did it"
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>What</span>
            <select value={kind} onChange={(event) => setKind(event.target.value as AuditKind)}>
              {Object.entries(AUDIT_KINDS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          {data && data.sites.length > 1 ? (
            <label className="field">
              <span>Site</span>
              <select value={siteId} onChange={(event) => setSiteId(event.target.value)}>
                <option value="">Every site</option>
                {data.sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="field">
            <span>Card owned by</span>
            <select value={department} onChange={(event) => setDepartment(event.target.value)}>
              <option value="">Any department</option>
              {data?.departments.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <div className="field-row field-dates">
            <label className="field">
              <span>From</span>
              <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </label>
            <label className="field">
              <span>To</span>
              <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </label>
          </div>
          <button type="submit" className="button" disabled={busy}>
            Search
          </button>
        </div>
      </form>

      {data ? (
        data.hits.length === 0 ? (
          <EmptyState icon="clipboard" title="Nothing in the log matches that" />
        ) : (
          <>
            <ol className="panel audit" aria-label="Audit log entries, newest first">
              {data.hits.map((hit) => (
                <Row key={hit.seq} hit={hit} nameOf={nameOf} />
              ))}
            </ol>
            {data.more !== null ? (
              <button
                type="button"
                className="button button-quiet"
                disabled={busy}
                onClick={() => void run(asked, data.more)}
              >
                Show older
              </button>
            ) : null}
          </>
        )
      ) : null}
    </section>
  );
}
